import React from 'react'

import CenteredAuthLayout from '../components/CenteredAuthLayout'

// Application-rejected lockout screen. A public, stateless page: a member of a
// rejected organization (organization.rejected_at set) is signed out and sent
// here by ApplicationController#lock_out_rejected_organization the moment they
// request an authenticated page, so they view this as a logged-out visitor.
// Because they're logged out, the Header renders in its normal logged-out
// (Sign In / Get Started) form and every link works on its own.
//
// Design: "Access Denied Versions.html" V1 (DeniedV1 in denied-gates.jsx) —
// calm slate badge, "Account status" eyebrow, heading, two short paragraphs.
// Tone is informational and final: no recourse CTAs. The org name from the
// design's body copy is dropped (generic "your organization") because the page
// is stateless — there is no current_user to read it from once signed out.
//
// The design's hex values map to @primer/react-brand base-color-scale tokens
// (see docs/design-tokens.md): #24292f → gray-9, #57606a → gray-6,
// #8a929c → gray-4 (nearest step), #eef1f4 → gray-1 (nearest step).
//
// Props: { membershipPath, signInPath, signUpPath }

// Calm "access ended" mark — a slate circle with a single horizontal stroke.
const EndedMark = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="var(--base-color-scale-gray-6)" strokeWidth="1.9" />
    <path d="M8 12h8" stroke="var(--base-color-scale-gray-6)" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
)

const RejectedScreen = ({ membershipPath, signInPath, signUpPath }) => {
  return (
    <CenteredAuthLayout
      maxWidth={520}
      membershipPath={membershipPath}
      signInPath={signInPath}
      signUpPath={signUpPath}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', fontFamily: 'var(--brand-body-fontFamily)' }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'var(--base-color-scale-gray-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EndedMark size={28} />
        </div>

        <div
          style={{
            marginTop: 20,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'var(--base-color-scale-gray-4)',
          }}
        >
          Account status
        </div>

        <h1
          style={{
            margin: '8px 0 0',
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.18,
            color: 'var(--base-color-scale-gray-9)',
          }}
        >
          Your organization no longer has access
        </h1>

        <p
          style={{
            margin: '16px 0 0',
            maxWidth: 470,
            fontSize: 15.5,
            lineHeight: 1.65,
            color: 'var(--base-color-scale-gray-6)',
          }}
        >
          After review, your organization hasn&rsquo;t been approved to use CivicLens. Access is
          granted only when an organization&rsquo;s use of CivicLens supports NCOC&rsquo;s mission to
          strengthen civic life in America.
        </p>

        <p
          style={{
            margin: '14px 0 0',
            maxWidth: 470,
            fontSize: 15.5,
            lineHeight: 1.65,
            color: 'var(--base-color-scale-gray-6)',
          }}
        >
          Your access has now ended. No further action is needed.
        </p>
      </div>
    </CenteredAuthLayout>
  )
}

export default RejectedScreen
